// ============================================================================
// TrendChart — a vivid, animated inline-SVG area/line chart for a month-bucketed
// business trend (sessions / hours / income over time).
// ----------------------------------------------------------------------------
// Pure SVG (no charting dependency), mirroring the locked dashboard
// ProgressSnapshot pattern: animated path-draw, gradient area fill, a pulsing
// end dot, and value-formatted Y ticks. Reduced-motion aware (paths snap to
// final, no draw). Responsive via viewBox + w-full.
//
// A single data point can't draw a line, so it degrades to an animated marker
// with its value — a one-month report still shows *something*.
// ============================================================================
import { useId, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import type { SeriesPoint } from "../../../api";
import { fmtMonth } from "../format";

export type ChartAccent = "coral" | "violet" | "ocean" | "lime";

const ACCENTS: Record<ChartAccent, { from: string; to: string }> = {
  coral: { from: "#FF5E62", to: "#FF9966" },
  violet: { from: "#6A5BFF", to: "#9D6BFF" },
  ocean: { from: "#2D8FFF", to: "#5B6BFF" },
  lime: { from: "#18C964", to: "#3BE085" },
};

const W = 560;
const H = 220;
const PAD = { top: 18, right: 18, bottom: 30, left: 48 };

export interface TrendChartProps {
  /** Month-bucketed points; x is a "YYYY-MM" string, y the value. */
  series: SeriesPoint[];
  accent?: ChartAccent;
  /** Render each Y value through this formatter (axis ticks + end label). */
  formatValue: (v: number) => string;
  /** Accessible label for the whole chart. */
  ariaLabel?: string;
  /** BCP-47 locale for month-tick formatting. */
  locale?: string;
}

/** A small line/area trend chart for one month-bucketed business series. */
export function TrendChart({
  series,
  accent = "coral",
  formatValue,
  ariaLabel,
  locale,
}: TrendChartProps) {
  const reduce = useReducedMotion();
  const uid = useId();
  const colors = ACCENTS[accent];

  const chart = useMemo(() => {
    if (series.length < 2) return null;
    const ys = series.map((p) => p.y);
    const rawLo = Math.min(...ys, 0); // anchor business trends at 0
    const rawHi = Math.max(...ys);
    const span = rawHi - rawLo || Math.max(Math.abs(rawHi), 1);
    const padY = span * 0.12;
    const lo = rawLo; // keep the baseline at the true floor (usually 0)
    const hi = rawHi + padY;
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const range = hi - lo || 1;

    const x = (i: number) => PAD.left + (innerW * i) / (series.length - 1);
    const y = (v: number) => PAD.top + innerH * (1 - (v - lo) / range);

    const linePath = series
      .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.y).toFixed(1)}`)
      .join(" ");
    const areaPath =
      `${linePath} L ${x(series.length - 1).toFixed(1)} ${(H - PAD.bottom).toFixed(1)}` +
      ` L ${x(0).toFixed(1)} ${(H - PAD.bottom).toFixed(1)} Z`;

    const ticks = [hi, (lo + hi) / 2, lo];

    return {
      x,
      y,
      linePath,
      areaPath,
      ticks,
      lastX: x(series.length - 1),
      lastY: y(ys[ys.length - 1]),
      firstX: x(0),
      lastIdx: series.length - 1,
    };
  }, [series]);

  // Single-point fallback: a centred animated marker with its value.
  if (!chart) {
    const only = series[0];
    if (!only) return null;
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 py-8"
        role="img"
        aria-label={ariaLabel}
      >
        <motion.span
          initial={reduce ? false : { scale: 0.7, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ type: "spring", stiffness: 320, damping: 20 }}
          className="nums font-display text-4xl font-extrabold tracking-tightest text-body"
        >
          {formatValue(only.y)}
        </motion.span>
        <span className="text-xs font-bold uppercase tracking-kicker text-body-faint">
          {fmtMonth(only.x, locale)}
        </span>
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="block w-full"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={`${uid}-area`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colors.from} stopOpacity="0.26" />
          <stop offset="100%" stopColor={colors.to} stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${uid}-line`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={colors.from} />
          <stop offset="100%" stopColor={colors.to} />
        </linearGradient>
      </defs>

      {/* Y gridlines + ticks */}
      {chart.ticks.map((tick, i) => (
        <g key={`${tick}-${i}`}>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={chart.y(tick)}
            y2={chart.y(tick)}
            stroke="#EAEDF5"
            strokeWidth="1"
          />
          <text
            x={PAD.left - 8}
            y={chart.y(tick) + 4}
            textAnchor="end"
            className="fill-body-faint text-[10px] font-bold"
          >
            {formatValue(tick)}
          </text>
        </g>
      ))}

      {/* X axis labels: first + last month */}
      <text
        x={chart.firstX}
        y={H - 8}
        textAnchor="start"
        className="fill-body-faint text-[10px] font-bold"
      >
        {fmtMonth(series[0].x, locale)}
      </text>
      <text
        x={chart.lastX}
        y={H - 8}
        textAnchor="end"
        className="fill-body-faint text-[10px] font-bold"
      >
        {fmtMonth(series[chart.lastIdx].x, locale)}
      </text>

      <motion.path
        d={chart.areaPath}
        fill={`url(#${uid}-area)`}
        initial={reduce ? false : { opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, delay: reduce ? 0 : 0.3 }}
      />
      <motion.path
        d={chart.linePath}
        fill="none"
        stroke={`url(#${uid}-line)`}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={reduce ? false : { pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 1.1, ease: [0.22, 0.61, 0.36, 1] }}
      />
      <motion.circle
        cx={chart.lastX}
        cy={chart.lastY}
        r="5"
        fill="#fff"
        stroke={colors.from}
        strokeWidth="3"
        initial={reduce ? false : { scale: 0 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{
          delay: reduce ? 0 : 1.05,
          type: "spring",
          stiffness: 400,
          damping: 18,
        }}
        style={
          { transformOrigin: "center", transformBox: "fill-box" } as React.CSSProperties
        }
      />
    </svg>
  );
}
